import { useMemo, useState, type FormEvent } from 'react';
import {
  CheckCircle2,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/lib/auth/store';

import { ApiErrorBanner } from './ApiErrorBanner';
import { StatusBadge } from './StatusBadge';
import {
  useAffectations,
  useApproveAffectation,
  useArchiveAffectation,
  useCreateAffectation,
  useFlowModels,
  useSegmentations,
} from './api';
import { ALL_GAP_TYPES, type AffectationRequest, type GapType } from './types';

const EMPTY: AffectationRequest = {
  segmentRowId: '',
  gapType: 'LIQUIDITY',
  flowModelRowId: '',
  horizonLabel: '',
  notes: '',
};

/**
 * Affectation matrix page. The list is a flat table sorted by segment + gap_type.
 * The create form fills in a routing for one (segment, gap_type) at a time —
 * the unique constraint on the server protects against double-routing.
 */
export function AffectationsPage() {
  const list = useAffectations();
  const segments = useSegmentations();
  const flowModels = useFlowModels();

  // Only ACTIVE flow models can be referenced.
  const activeFlowModels = useMemo(
    () => (flowModels.data ?? []).filter((fm) => fm.status === 'ACTIVE'),
    [flowModels.data],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Affectations</h1>
          <p className="text-sm text-muted-foreground">
            Routes each (Segment × Gap Type) pair to a Flow Model. One active row per pair.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          {list.isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Reload
        </Button>
      </header>

      <CreateForm
        segments={segments.data ?? []}
        flowModels={activeFlowModels}
        loading={segments.isLoading || flowModels.isLoading}
      />

      {list.isError ? <ApiErrorBanner error={list.error} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All affectations</CardTitle>
          <CardDescription>
            {list.data ? `${list.data.length} row(s)` : 'Loading…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table rows={list.data ?? []} loading={list.isLoading} />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Create form ────────────────────────────────────────────────────

function CreateForm({
  segments,
  flowModels,
  loading,
}: {
  segments: Array<{ id: string; segmentId: number; segmentName: string; status: string }>;
  flowModels: Array<{ id: string; code: string; name: string }>;
  loading: boolean;
}) {
  const create = useCreateAffectation();
  const [form, setForm] = useState<AffectationRequest>(EMPTY);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    create.mutate(
      {
        ...form,
        horizonLabel: form.horizonLabel || undefined,
        notes: form.notes || undefined,
      },
      {
        onSuccess: () => setForm(EMPTY),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">New affectation</CardTitle>
        <CardDescription>
          Pick one segment, one gap type, and the active flow model that should drive its
          amortization for that gap. Status starts as DRAFT — an admin must approve.
          <br />
          <span className="text-amber-700">
            Only <strong>ACTIVE</strong> flow models appear in the dropdown. A newly created
            flow model starts as DRAFT — approve it on the Flow Models page first to make
            it pickable here.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-3 md:grid-cols-3" onSubmit={handleSubmit}>
          <Field label="Segment" htmlFor="aff-seg">
            <select
              id="aff-seg"
              required
              value={form.segmentRowId}
              onChange={(e) => setForm({ ...form, segmentRowId: e.target.value })}
              className={selectCls}
              disabled={loading}
            >
              <option value="">— pick a segment —</option>
              {segments
                .filter((s) => s.status !== 'ARCHIVED')
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    #{s.segmentId} {s.segmentName}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Gap type" htmlFor="aff-gap">
            <select
              id="aff-gap"
              value={form.gapType}
              onChange={(e) =>
                setForm({ ...form, gapType: e.target.value as GapType })
              }
              className={selectCls}
            >
              {ALL_GAP_TYPES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Flow model" htmlFor="aff-fm">
            <select
              id="aff-fm"
              required
              value={form.flowModelRowId}
              onChange={(e) => setForm({ ...form, flowModelRowId: e.target.value })}
              className={selectCls}
              disabled={loading}
            >
              <option value="">— pick a flow model —</option>
              {flowModels.map((fm) => (
                <option key={fm.id} value={fm.id}>
                  [{fm.code}] {fm.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Horizon label (free-form)" htmlFor="aff-horizon">
            <Input
              id="aff-horizon"
              placeholder="1Y · 5Y · 30D"
              value={form.horizonLabel}
              onChange={(e) => setForm({ ...form, horizonLabel: e.target.value })}
            />
          </Field>
          <Field label="Notes" htmlFor="aff-notes" className="md:col-span-2">
            <Input
              id="aff-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Field>

          <div className="md:col-span-3 space-y-2">
            {create.isError ? <ApiErrorBanner error={create.error} /> : null}
            {create.isSuccess ? (
              <p className="flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> Affectation created.
              </p>
            ) : null}
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Create affectation
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Table ──────────────────────────────────────────────────────────

function Table({
  rows,
  loading,
}: {
  rows: import('./types').Affectation[];
  loading: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Segment</th>
            <th className="px-4 py-2 text-left font-medium">Gap type</th>
            <th className="px-4 py-2 text-left font-medium">Flow model</th>
            <th className="px-4 py-2 text-left font-medium">Horizon</th>
            <th className="px-4 py-2 text-left font-medium">Status</th>
            <th className="px-4 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                No affectations yet — create one above.
              </td>
            </tr>
          ) : (
            rows.map((a) => <Row key={a.id} a={a} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

function Row({ a }: { a: import('./types').Affectation }) {
  const approve = useApproveAffectation(a.id);
  const archive = useArchiveAffectation(a.id);
  const isAdmin = useAuthStore((x) => x.hasRole('ALM_ADMIN'));

  return (
    <tr className="border-b last:border-0 align-top">
      <td className="px-4 py-2">
        <span className="font-mono text-xs text-muted-foreground">#{a.segmentId}</span>{' '}
        <span className="font-medium">{a.segmentName}</span>
      </td>
      <td className="px-4 py-2">
        <span className="rounded bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground">
          {a.gapType}
        </span>
      </td>
      <td className="px-4 py-2">
        <span className="font-mono text-xs text-muted-foreground">[{a.flowModelCode}]</span>{' '}
        {a.flowModelName}
      </td>
      <td className="px-4 py-2 text-muted-foreground">{a.horizonLabel ?? '—'}</td>
      <td className="px-4 py-2">
        <StatusBadge status={a.status} />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          {isAdmin && a.status !== 'ACTIVE' && a.status !== 'ARCHIVED' ? (
            <Button
              variant="ghost"
              size="sm"
              title="Approve"
              disabled={approve.isPending}
              onClick={() => approve.mutate()}
            >
              <ShieldCheck className="h-4 w-4 text-primary" />
            </Button>
          ) : null}
          {isAdmin && a.status !== 'ARCHIVED' ? (
            <Button
              variant="ghost"
              size="sm"
              title="Archive"
              disabled={archive.isPending}
              onClick={() => archive.mutate()}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

const selectCls =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

function Field({
  label,
  htmlFor,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={'space-y-1.5 ' + (className ?? '')}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}
